import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import {
  autocompleteSuggestionsT,
  deleteAutocompleteSuggestionT,
  DeleteAutocompleteSuggestion,
  INVOICE_LINE_DESCRIPTION_KEY,
  NO_PRODUCT_KEY,
  SplitAutocompleteSuggestions,
  splitAutocompleteSuggestions,
} from "dora-contracts";
import { createErrorReportingAsyncThunk } from "../../helpers";
import axios from "../../../axios";

const prefix = "data/autocomplete-suggestions";

type AutocompleteSuggestionsState = SplitAutocompleteSuggestions;

const initialState: AutocompleteSuggestionsState = {
  fields: {},
  invoiceLineDescriptions: {},
};

export const productDescriptionsKey = (productId: string | null) =>
  productId ?? NO_PRODUCT_KEY;

export const loadAutocompleteSuggestions = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async () => {
    const res = await axios.get("/api/autocomplete-suggestions");
    const record = await tPromise.decode(autocompleteSuggestionsT, res.data);
    return splitAutocompleteSuggestions(record);
  }
);

export const deleteAutocompleteSuggestion = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (arg: DeleteAutocompleteSuggestion, { dispatch }) => {
    dispatch(removeValue(arg));
    await axios.delete("/api/autocomplete-suggestions", {
      data: deleteAutocompleteSuggestionT.encode(arg),
    });
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    // Mirrors the server so a description saved on a line is suggested on
    // the next line without reloading the suggestions.
    invoiceLineDescriptionRemembered: (
      state,
      action: PayloadAction<{ productId: string | null; description: string }>
    ) => {
      const key = productDescriptionsKey(action.payload.productId);
      const description = action.payload.description.trim();
      if (!description) return;
      state.invoiceLineDescriptions[key] = [
        description,
        ...(state.invoiceLineDescriptions[key] ?? []).filter(
          (v) => v !== description
        ),
      ];
    },
    removeValue: (state, action: PayloadAction<DeleteAutocompleteSuggestion>) => {
      const { key, value, productId } = action.payload;
      const [lists, listKey] =
        key === INVOICE_LINE_DESCRIPTION_KEY
          ? [state.invoiceLineDescriptions, productDescriptionsKey(productId ?? null)]
          : [state.fields, key];
      if (lists[listKey]) {
        lists[listKey] = lists[listKey].filter((v) => v !== value);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadAutocompleteSuggestions.fulfilled, (_, action) => {
      return action.payload;
    });
  },
});

export const { invoiceLineDescriptionRemembered, removeValue } = slice.actions;
export default slice.reducer;
